import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export interface DocumentFolder {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  document_count: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Document {
  id: string;
  folder_id: string | null;
  title: string;
  description: string | null;
  document_type: 'file' | 'link';
  external_url: string | null;
  current_version: number;
  file_name: string | null;
  file_size: number | null;
  content_type: string | null;
  folder_name: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string | null;
  change_note: string | null;
  created_at: string;
}

// ============================================================================
// Folder Hooks
// ============================================================================

export function useDocumentFolders() {
  return useQuery({
    queryKey: ['document-folders'],
    queryFn: async () => {
      const response = await api.documents.folders.list();
      return {
        folders: response.folders as DocumentFolder[],
        unfiled_count: response.unfiled_count,
      };
    },
  });
}

export function useCreateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string; sort_order?: number }) =>
      api.documents.folders.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('Folder created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create folder');
    },
  });
}

export function useUpdateFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; description?: string; sort_order?: number }) =>
      api.documents.folders.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('Folder updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update folder');
    },
  });
}

export function useDeleteFolder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.documents.folders.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Folder deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete folder');
    },
  });
}

// ============================================================================
// Document Hooks
// ============================================================================

export function useDocuments(params?: { folder_id?: string; document_type?: string; search?: string; project_id?: string }) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: async () => {
      const response = await api.documents.list(params);
      return response.documents as Document[];
    },
  });
}

export function useDocument(id: string | null) {
  return useQuery({
    queryKey: ['document', id],
    queryFn: async () => {
      const response = await api.documents.get(id!);
      return {
        document: response.document as Document,
        versions: response.versions as DocumentVersion[],
      };
    },
    enabled: !!id,
  });
}

export function useCreateLinkDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { title: string; description?: string; folder_id?: string; project_id?: string; external_url: string }) =>
      api.documents.createLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('Link added');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create link');
    },
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, title, description, folderId, projectId }: {
      file: File; title: string; description?: string; folderId?: string; projectId?: string;
    }) => api.documents.upload(file, title, description, folderId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('File uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload file');
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string; title?: string; description?: string; folder_id?: string; external_url?: string;
    }) => api.documents.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('Document updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update document');
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.documents.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      toast.success('Document deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete document');
    },
  });
}

export function useUploadVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, file, changeNote }: {
      documentId: string; file: File; changeNote?: string;
    }) => api.documents.uploadVersion(documentId, file, changeNote),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', variables.documentId] });
      toast.success('New version uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload version');
    },
  });
}
